import { z } from "zod";
import { TimeSlot } from "@carry/shared";
import { PublicIdSchema } from "./common";
import { VehicleInspectionItems } from "./driver-inspection";
import { RouteStatus } from "./route";

export const DailyReportStatus = z.enum(["draft", "submitted", "approved", "rejected"]);
export const DailyReportWeather = z.enum(["sunny", "cloudy", "rainy", "snowy", "other"]);
export const DailyReportAlcoholResult = z.enum(["pass", "fail"]);
export const DailyReportInspectionResult = z.enum(["ok", "ng", "not_recorded"]);

/** 集約点検結果の表示ラベル。ok=異常なし / ng=異常あり / not_recorded=未実施。 */
export const DAILY_REPORT_INSPECTION_RESULT_LABELS = {
  ok: "異常なし",
  ng: "異常あり",
  not_recorded: "未実施",
} as const satisfies Record<z.infer<typeof DailyReportInspectionResult>, string>;

export const DriverDailyReportDeliveryStop = z.object({
  route_id: PublicIdSchema,
  stop_id: PublicIdSchema,
  slot: TimeSlot,
  sequence: z.number().int(),
  stop_type: z.string(),
  status: z.string(),
  name: z.string().nullable(),
  address: z.string().nullable(),
  client_name: z.string().nullable(),
  planned_time: z.iso.datetime({ offset: true }).nullable(),
  /** 到着実績。表示は従来どおりこちらのみ (`actual_departure` は #842 の修正フォームの初期値専用)。 */
  actual_time: z.iso.datetime({ offset: true }).nullable(),
  /** #842: 完了実績。日報差戻し時の実績修正フォームで到着・完了の両方をプリフィルするために追加。 */
  actual_departure: z.iso.datetime({ offset: true }).nullable().optional().default(null),
  /** 後追い登録で完了した時刻。当日打刻は null。管理画面の日報明細バッジに使う */
  backfilled_at: z.iso.datetime({ offset: true }).nullable().optional().default(null),
});

export const DriverDailyReportExpense = z.object({
  public_id: PublicIdSchema,
  expense_type: z.enum(["highway", "parking", "other"]),
  payment_method: z.enum(["cash", "credit_card", "invoice", "accounts_receivable"]),
  amount: z.number().int().min(0),
  description: z.string().nullable(),
  incurred_on: z.iso.date(),
  /** 領収書画像の presigned URL (未アップロードは null)。 */
  receipt_image_url: z.string().nullable(),
  /** #616: 経費が発生した配達先 (route_stop)。未紐付けは null。 */
  route_stop_public_id: PublicIdSchema.nullable().default(null),
  /** #616: 配達先に紐付く注文。未紐付け・注文なし stop は null。 */
  order_public_id: PublicIdSchema.nullable().default(null),
  /** #616: 表示用の配達先名 (未紐付けは null)。 */
  stop_name: z.string().nullable().default(null),
  /** #616: 表示用の受注番号 (未紐付け・未設定は null)。 */
  order_number: z.string().nullable().default(null),
  /** #616: 事務による代理入力なら true。締め後に補完された経費を日報上で判別する。 */
  entered_by_proxy: z.boolean().default(false),
});

export const DriverDailyReport = z.object({
  public_id: PublicIdSchema,
  driver_public_id: PublicIdSchema,
  work_date: z.iso.date(),
  status: DailyReportStatus,
  delivery_count: z.number().int().min(0),
  /** ODO差またはF08承認補正として driver_daily_reports に保存した日次距離。 */
  total_distance_km: z.string().nullable(),
  /**
   * 乖離判定・画面表示に使う実走行距離。
   * #627: 距離突合 (F27) の廃止で出典は常に出退勤 ODO になったため distance_source は撤去した。
   */
  actual_distance_km: z.string().nullable().optional(),
  remarks: z.string().nullable(),
  rejection_note: z.string().nullable(),
  approved_by: PublicIdSchema.nullable(),
  rejected_by: PublicIdSchema.nullable(),
  submitted_at: z.iso.datetime({ offset: true }).nullable(),
  clock_in_at: z.iso.datetime({ offset: true }).nullable(),
  clock_out_at: z.iso.datetime({ offset: true }).nullable(),
  /**
   * 勤務時間 (退勤 - 出勤) から算出した休憩時間 (分)。出退勤いずれか未打刻なら null。
   * 正社員は改善基準告示「430」(4 時間ごと 30 分) と労基法休憩の大きい方、それ以外は労基法休憩。
   * 算出ロジック: apps/api/src/services/daily-report-break.ts
   */
  break_minutes: z.number().int().nullable(),
  weather: DailyReportWeather.nullable(),
  odometer_start: z.number().int().nullable(),
  odometer_end: z.number().int().nullable(),
  vehicle_public_id: PublicIdSchema.nullable(),
  vehicle_number: z.string().nullable(),
  route_time_slots: z.array(TimeSlot).default([]),
  planned_distance_km: z.number().nullable(),
  distance_diff_pct: z.number().nullable(),
  /** 未承認時は対象勤怠の最新アルコールチェック。承認後は承認時点の保存値。 */
  alcohol_result: DailyReportAlcoholResult.nullable(),
  alcohol_measured_value: z.string().nullable(),
  /** 未承認時は対象勤怠の最新車両点検を集計した値。承認後は承認時点の保存値。 */
  inspection_result: DailyReportInspectionResult.nullable(),
  /** 未承認時は対象勤怠の最新車両点検 12 項目。承認後は承認時点の保存値。未記録時は null。 */
  inspection_items: VehicleInspectionItems.nullable(),
  /** 未承認時は対象勤怠の最新点検備考。承認後は承認時点の保存値。未記録または未入力時は null。 */
  inspection_notes: z.string().nullable(),
  refueling_amount_yen: z.number().int().nullable(),
  delivery_history: z.array(DriverDailyReportDeliveryStop).default([]),
  /** 当該ドライバー・当日の経費 (領収書 presigned URL 付き)。管理画面の日報確認で表示する。 */
  expenses: z.array(DriverDailyReportExpense).default([]),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const SubmitDailyReportInput = z.object({
  /**
   * 対象日 (YYYY-MM-DD)。省略時は当日。指定する場合も当日のみ受理する。
   * 過去日報の欠損復旧は RecoverDailyReportInput を使う。
   */
  work_date: z.iso.date().optional(),
  delivery_count: z.number().int().min(0).optional(),
  total_distance_km: z.string().optional(),
  remarks: z.string().optional(),
});

/**
 * #829: 管理者 (dispatcher) が運転日報を編集する入力。
 *
 * ドライバーが提出する 3 項目だけを対象にする。アルコールチェック・車両点検は**実測の記録**で、
 * 第三者が書き換えると証跡として成立しないため対象外 (承認時のスナップショットも触らない)。
 *
 * 部分更新。省略した項目は現在値を維持する。`remarks` は null で明示的に空へ戻せる
 * (空文字と「変更しない」を区別するため undefined と null を分ける)。
 *
 * 編集できる状態は限定しない (`approved` を含む全ステータス)。承認後に誤りが見つかったときに
 * 直せないと運用が詰むため。運転日報は請求に流れない (F29・請求書は `driver_daily_reports` を
 * 参照しない) ので、締め済みの月でもガードしない。変更は監査ログで追う。
 */
export const UpdateDailyReportInput = z
  .object({
    delivery_count: z.number().int().min(0).optional(),
    /** 実走行距離 (km)。数値として解釈できる文字列。null で未入力へ戻す */
    total_distance_km: z.string().nullable().optional(),
    remarks: z.string().nullable().optional(),
  })
  // strict: 対象外の項目 (alcohol_result / inspection_* など) を送られたら 400 で弾く。
  // 実測の記録が「送れば通る」状態だと、UI を直しただけで書き換えられてしまう。
  .strict();

export const DAILY_REPORT_RECOVERY_ROUTE_STATUSES = [
  "assigned",
  "confirmed",
  "started",
  "completed",
] as const satisfies readonly z.infer<typeof RouteStatus>[];

/** 過去日報の復旧根拠として扱えるルート状態。将来の RouteStatus 追加は自動許可しない。 */
export const DailyReportRecoveryRouteStatus = z.enum(DAILY_REPORT_RECOVERY_ROUTE_STATUSES);

export const DailyReportRecoveryRoutePreview = z.object({
  public_id: PublicIdSchema,
  // #425: レーンは日単位。代表便の導出値で、導出不能時は null。
  time_slot: TimeSlot.nullable(),
  status: DailyReportRecoveryRouteStatus,
});

export const DailyReportRecoveryCandidate = z.object({
  work_date: z.iso.date(),
  clock_in_at: z.iso.datetime({ offset: true }),
  clock_out_at: z.iso.datetime({ offset: true }),
  vehicle_public_id: PublicIdSchema.nullable(),
  vehicle_number: z.string().nullable(),
  routes: z.array(DailyReportRecoveryRoutePreview).min(1),
  delivery_count: z.number().int().min(0),
  total_distance_km: z.string().nullable(),
});

export const ListDailyReportRecoveryCandidatesInput = z.object({
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

/** 集計値はサーバで再計算するため、クライアント入力は対象日と任意の備考だけに限定する。 */
export const RecoverDailyReportInput = z
  .object({
    work_date: z.iso.date(),
    remarks: z.string().optional(),
  })
  .strict();

/** 復旧対象の日報が既に存在する場合に409 dataとして返す共有プロトコル。 */
export const DailyReportAlreadyExistsErrorData = z
  .object({
    code: z.literal("DAILY_REPORT_ALREADY_EXISTS"),
    existing_report: z
      .object({
        public_id: PublicIdSchema,
        status: DailyReportStatus,
      })
      .strict(),
  })
  .strict();

/** 差戻し日報の再提出でドライバーが修正できる項目。対象日報は public_id で特定する。 */
export const ResubmitDailyReportInput = SubmitDailyReportInput.omit({ work_date: true });

export const ListDriverDailyReportsInput = z.object({
  /** YYYY-MM. 未指定ならハンドラ側で当月を補完 (6.40 月選択) */
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  status: DailyReportStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type DriverDailyReportDto = z.infer<typeof DriverDailyReport>;
export type DriverDailyReportDeliveryStopDto = z.infer<typeof DriverDailyReportDeliveryStop>;
export type DriverDailyReportExpenseDto = z.infer<typeof DriverDailyReportExpense>;
export type SubmitDailyReportInputDto = z.infer<typeof SubmitDailyReportInput>;
export type DailyReportRecoveryCandidateDto = z.infer<typeof DailyReportRecoveryCandidate>;
export type DailyReportRecoveryRoutePreviewDto = z.infer<typeof DailyReportRecoveryRoutePreview>;
export type DailyReportRecoveryRouteStatusDto = z.infer<typeof DailyReportRecoveryRouteStatus>;
export type ListDailyReportRecoveryCandidatesInputDto = z.infer<
  typeof ListDailyReportRecoveryCandidatesInput
>;
export type RecoverDailyReportInputDto = z.infer<typeof RecoverDailyReportInput>;
export type DailyReportAlreadyExistsErrorDataDto = z.infer<
  typeof DailyReportAlreadyExistsErrorData
>;
export type ResubmitDailyReportInputDto = z.infer<typeof ResubmitDailyReportInput>;
export type DailyReportStatusDto = z.infer<typeof DailyReportStatus>;
export type ListDriverDailyReportsInputDto = z.infer<typeof ListDriverDailyReportsInput>;
